/**
 * Shared filename and file-ID extraction helpers for clinical document
 * timeline events (SDI, RDI, and future doc types).
 *
 * Both SDI and RDI store form data under the same flat shape
 * (`payload.formData.patientName` / `payload.formData.date`) and store their
 * PDF file ID under `payload.files.<docTypePdf>.patientFileId`.
 */

/**
 * Build a display filename for a clinical document PDF version.
 *
 * @param docTypePrefix  Upper-case doc-type prefix, e.g. `"SDI"` or `"RDI"`.
 * @param pdfKey         The key under `payload.files`, e.g. `"sdiPdf"` or `"rdiPdf"`.
 * @param payload        The raw timeline event payload (may be undefined).
 * @param version        Monotonically increasing version number (1-based).
 */
export function buildClinicalFilename(
    docTypePrefix: string,
    pdfKey: string,
    payload: Record<string, unknown> | undefined,
    version: number
): string {
    // Documents imported off Monday carry no formData to synthesise a name from, so
    // they record the real filename on the file ref. Prefer it when present.
    const files = payload?.files as Record<string, unknown> | undefined;
    const pdfEntry = files?.[pdfKey] as Record<string, unknown> | undefined;
    const imported = pdfEntry?.originalFilename;
    if (typeof imported === 'string' && imported.trim().length > 0) return imported;

    const formData = payload?.formData as Record<string, unknown> | undefined;
    const name = (formData?.patientName as string) ?? '';
    const date = (formData?.date as string) ?? '';
    const sanitized = name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^\w.-]/g, '');
    const versionSuffix = version > 1 ? `-v${version}` : '';

    if (sanitized && date) {
        return `${docTypePrefix}-${sanitized}-${date}${versionSuffix}.pdf`;
    }
    return `${docTypePrefix} v${version}.pdf`;
}

/**
 * Extract the patient-file ID for a clinical document PDF from the event
 * payload's `files` map.
 *
 * @param pdfKey  The key under `payload.files`, e.g. `"sdiPdf"` or `"rdiPdf"`.
 * @param payload The raw timeline event payload (may be undefined).
 */
export function extractClinicalPatientFileId(
    pdfKey: string,
    payload: Record<string, unknown> | undefined
): string | null {
    const files = payload?.files as Record<string, unknown> | undefined;
    const pdfEntry = files?.[pdfKey] as Record<string, unknown> | undefined;
    const id = pdfEntry?.patientFileId;
    return typeof id === 'string' ? id : null;
}
